// Single source of truth for "can this account use the app?".
//
// Before this, the web app (App.jsx) and the mobile app (app/_layout.tsx) each
// carried their own copy of the rule and read subscription state from two
// different tables — trial_ends_at off profiles, subscription_status off
// organizations — which could disagree. Both clients now call decideAccess()
// with values read from the organization row.
//
// Nothing here enforces anything: it decides what the UI shows. The database
// does not yet check subscription state, so this is a curtain, not a lock.

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'cancelled'
  | 'comped';

export type AccessReason =
  | 'ok'
  | 'superadmin'
  | 'comped'
  | 'active'
  | 'onboarding'
  | 'in_grace'
  | 'suspended'
  | 'trial_expired'
  | 'subscription_lapsed';

export interface AccessInput {
  role: string | null | undefined;
  subscriptionStatus: SubscriptionStatus | string | null | undefined;
  trialEndsAt: Date | string | null | undefined;
  /** organizations.suspended_at — a superadmin block, independent of payment. */
  suspendedAt?: Date | string | null;
  /** organizations.grace_until — set by the Paystack webhook on a failed charge. */
  graceUntil?: Date | string | null;
  /** profiles.onboarding_completed === false means they never finished setup. */
  onboardingCompleted?: boolean | null;
  /** Injectable for tests. */
  now?: Date;
}

export interface AccessDecision {
  locked: boolean;
  reason: AccessReason;
  /** Copy for the lock screen. Empty when not locked. */
  message: string;
  /** True while a lapsed payer is inside their grace window. */
  inGrace: boolean;
  graceEndsAt: Date | null;
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function decideAccess(input: AccessInput): AccessDecision {
  const now = input.now ?? new Date();
  const trialEndsAt = toDate(input.trialEndsAt);
  const suspendedAt = toDate(input.suspendedAt);
  const graceUntil = toDate(input.graceUntil);

  const inGrace = !!graceUntil && graceUntil.getTime() > now.getTime();
  const base = { inGrace, graceEndsAt: graceUntil };
  const open = (reason: AccessReason): AccessDecision =>
    ({ locked: false, reason, message: '', ...base });

  // HQ staff are never locked out — they have to be able to fix billing.
  if (input.role === 'superadmin') return open('superadmin');

  // A manual suspension outranks everything below, including an active
  // subscription: it is the lever for abuse, disputes and non-payment outside
  // Paystack. It deliberately does not outrank superadmin.
  if (suspendedAt) {
    return {
      locked: true,
      reason: 'suspended',
      message: 'This account has been suspended. Please contact support.',
      ...base,
    };
  }

  if (input.subscriptionStatus === 'comped') return open('comped');
  if (input.subscriptionStatus === 'active') return open('active');

  // Someone who never finished setup has no meaningful trial yet; let them
  // through to the wizard rather than trapping them on a billing screen.
  if (input.onboardingCompleted === false) return open('onboarding');

  const lapsed =
    input.subscriptionStatus === 'past_due' || input.subscriptionStatus === 'cancelled';
  const trialExpired = !!trialEndsAt && now.getTime() > trialEndsAt.getTime();

  if (!lapsed && !trialExpired) return open('ok');

  // Grace covers a failed charge, not an expired trial — a trial that ran out
  // was already the grace period.
  if (lapsed && !trialExpired && inGrace) return open('in_grace');

  if (lapsed) {
    return {
      locked: true,
      reason: 'subscription_lapsed',
      message: 'Your subscription is not active. Subscribe below to continue using PetroTrack.',
      ...base,
    };
  }

  return {
    locked: true,
    reason: 'trial_expired',
    message: 'Your trial has ended. Subscribe below to continue using PetroTrack.',
    ...base,
  };
}

/** Days of access granted after a charge fails. The webhook stamps grace_until. */
export const DEFAULT_GRACE_DAYS = 7;
